import { useState, type ReactNode } from "react";

// By path, not through `@/ui`: nothing here is allowed to depend on the overlay
// layer, which is mounted *inside* this gate.
import { Spinner } from "@/ui/Spinner";
import { enterProps } from "@/ui/motion";
import { LoginScreen } from "./LoginScreen";
import { ServerUnreachable } from "./ServerUnreachable";
import { useSession } from "./SessionContext";

/**
 * Renders `children` only once there is a live session.
 *
 * The three gates before that are distinct on purpose — connecting, backend
 * down, and not signed in are three different problems with three different
 * fixes, and collapsing them is how an outage ends up looking like an empty
 * task list.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { status, connectionError, reconnect } = useSession();
  const [retrying, setRetrying] = useState(false);

  async function handleRetry() {
    setRetrying(true);
    try {
      await reconnect();
    } finally {
      setRetrying(false);
    }
  }

  if (status === "connecting") {
    return (
      <div
        {...enterProps("content")}
        className="flex min-h-full flex-col items-center justify-center gap-pad-tight bg-bg"
      >
        <Spinner size="lg" label="Connecting to the Kobo server" />
        <p className="text-caption text-subtle">Connecting…</p>
      </div>
    );
  }

  if (status === "unreachable") {
    return (
      <div className="flex min-h-full bg-bg">
        <ServerUnreachable detail={connectionError} onRetry={() => void handleRetry()} retrying={retrying} />
      </div>
    );
  }

  if (status === "signed-out") {
    return <LoginScreen />;
  }

  return <>{children}</>;
}
